// import { encode } from "js-base64";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useMutation, UseMutationResult } from "react-query";
import md5 from "md5";
import { HttpError } from "@mb-pro-ui/utils";
import { useUserInfo } from "./user-info";
import { useApi } from "./ApiProvider";
import { clearDevices } from "./DevicesProvider";

import { paths } from "@helios/toolbox-types";
import { eq } from "lodash-es";
export type UserData = paths["/login"]["get"]["responses"]["200"]["content"]["application/json"];
export type TestTokenResponse = paths["/test-token"]["get"]["responses"]["200"]["content"]["application/json"];

interface SigninOptions {
  username: string;
  password: string;
}

interface Auth {
  user: UserData | null;
  signin: UseMutationResult<UserData, HttpError, SigninOptions>;
  signout: UseMutationResult<void, HttpError, void>;
}

const AuthContext = createContext<Auth | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren<{}>) {
  const [user, setUser] = useUserInfo();
  const userChecked = useRef(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const onSigninSuccess = useCallback(
    (userResponse: UserData, _user: SigninOptions) => {
      if (mounted.current) {
        setUser(userResponse);
      }
    },
    [setUser, mounted]
  );

  const onSignoutSettled = useCallback(() => {
    clearDevices();
    if (mounted.current) {
      setUser(null);
    }
  }, [setUser, mounted]);

  const api = useApi();

  const loginApi = useCallback(
    ({ username, password }: SigninOptions) => {
      return api<UserData>("/login", {
        headers: {
          username,
          password: md5(password),
        },
      }).catch((err) => {
        console.error(err);
        throw err;
      });
    },
    [api]
  );

  const logoutApi = useCallback(() => Promise.resolve(), []);

  const signin = useMutation<UserData, HttpError, SigninOptions>(loginApi, {
    onSuccess: onSigninSuccess,
  });

  const signout = useMutation<void, HttpError, void>(logoutApi, {
    onSettled: onSignoutSettled,
  });

  useEffect(() => {
    if (user && !user.rights) {
      signout.mutate();
      return;
    }
    if (user && !userChecked.current) {
      userChecked.current = true;
      api<TestTokenResponse>("/test-token", {
        headers: { token: user.token },
      })
        .then((response) => {
          if (!eq(response, user) && mounted.current) {
            setUser(response);
          }
        })
        .catch(signout.mutate);
    }
  }, [user, signout, api, setUser]);

  const value = useMemo(
    () => ({
      user: user ? { ...user, rights: { ...user.rights } } : user,
      signin,
      signout,
    }),
    [user, signin, signout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (auth === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return auth;
}
